import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Derives the WebSocket origin from API_BASE_URL -- handles both an absolute dev URL
// (http://localhost:8000/api -> ws://localhost:8000) and a relative production one (/api ->
// wss://<current host>, since a relative URL has no scheme/host of its own to convert).
const resolveWsBaseUrl = () => {
    if (/^https?:\/\//.test(API_BASE_URL)) {
        return API_BASE_URL.replace(/^http/, 'ws').replace(/\/api\/?$/, '');
    }
    const wsScheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${wsScheme}://${window.location.host}`;
};
const WS_BASE_URL = resolveWsBaseUrl();

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ============= Announcements API Functions =============

/**
 * Get the list of announcements (Authenticated users)
 * @returns {Promise} Array of announcements
 */
export const getAnnouncements = async () => {
    try {
        const response = await api.get('/announcements/');
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Long-polls for announcements newer than `afterId` -- the request stays open server-side
 * (up to ~25s) until a new one is created, then resolves immediately with it, or resolves with
 * an empty array once the server-side timeout elapses. Callers should re-issue this in a loop
 * (see Notification.jsx) to get near-real-time updates without a fixed polling interval.
 * @param {number} afterId - Only announcements with an id greater than this are returned
 * @returns {Promise<Array>} Newly created announcements, if any
 */
export const waitForNewAnnouncements = async (afterId) => {
    try {
        // Client-side timeout kept comfortably above the server's own ~25s wait, so a slow
        // response is given the chance to arrive normally rather than being aborted right as
        // the server was about to answer.
        const response = await api.get('/announcements/wait/', {
            params: { after_id: afterId },
            timeout: 35000,
        });
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a specific announcement by ID (Authenticated users)
 * @param {number} announcementId - ID of the announcement to retrieve
 * @returns {Promise} The announcement object
 */
export const getAnnouncement = async (announcementId) => {
    try {
        const response = await api.get(`/announcements/${announcementId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new announcement (Superuser only)
 * @param {Object} announcementData - Data for the new announcement
 * @param {string} announcementData.subject - Subject of the announcement
 * @param {string} announcementData.message - Message content of the announcement
 * @param {boolean} announcementData.is_important - Whether the announcement is marked as important
 *
 * @returns {Promise} The created announcement object
 * */
export const createAnnouncement = async (announcementData) => {
    try {
        const response = await api.post('/announcements/', announcementData);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing announcement (Superuser only)
 * @param {number} announcementId - ID of the announcement to update
 * @param {Object} announcementData - Updated data for the announcement
 * @param {string} announcementData.subject - Updated subject of the announcement
 * @param {string} announcementData.message - Updated message content of the announcement
 * @param {boolean} announcementData.is_important - Updated importance status of the announcement
 * @returns {Promise} The updated announcement object
 */
export const updateAnnouncement = async (announcementId, announcementData) => {
    try {
        const response = await api.put(`/announcements/${announcementId}/`, announcementData);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteAnnouncement = async (announcementId) => {
    try {
        const response = await api.delete(`/announcements/${announcementId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// ============= Notification Messages API Functions =============
// Personal, single-recipient notifications -- unlike Announcements (which broadcast to
// everyone), each of these is addressed to exactly one account.

/**
 * Get the authenticated user's own received notification messages (Authenticated users)
 * @returns {Promise<Array>} Array of notification messages
 */
export const getNotificationMessages = async () => {
    try {
        const response = await api.get('/notifications/');
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a specific notification message by ID
 * @param {number} notificationId - ID of the notification message to retrieve
 * @returns {Promise} The notification message object
 */
export const getNotificationMessage = async (notificationId) => {
    try {
        const response = await api.get(`/notifications/${notificationId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new notification message targeted at a specific user (Superuser only)
 * @param {Object} notificationData
 * @param {string} notificationData.subject - Subject of the notification
 * @param {string} notificationData.message - Message content of the notification
 * @param {boolean} [notificationData.is_important] - Whether the notification is marked as important
 * @param {number} notificationData.receiver - User id of the recipient
 * @returns {Promise} The created notification message object
 */
export const createNotificationMessage = async (notificationData) => {
    try {
        const response = await api.post('/notifications/', notificationData);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing notification message (Superuser only)
 * @param {number} notificationId - ID of the notification message to update
 * @param {Object} notificationData - Updated data for the notification message
 * @returns {Promise} The updated notification message object
 */
export const updateNotificationMessage = async (notificationId, notificationData) => {
    try {
        const response = await api.put(`/notifications/${notificationId}/`, notificationData);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a notification message -- a superuser managing it, or the receiver dismissing their own
 * (there's no separate read/unread flag on this model, so deleting is how a recipient clears it
 * from their inbox).
 * @param {number} notificationId
 */
export const deleteNotificationMessage = async (notificationId) => {
    try {
        const response = await api.delete(`/notifications/${notificationId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// ============= User Contacts API Functions =============

/**
 * Get the list of the caller's user contacts (Authenticated users)
 * @returns {Promise<Array>} Array of user contacts
 */
export const getUserContacts = async () => {
    try {
        const response = await api.get('/chat/user-contacts/');
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Add a contact to the authenticated user's own contact list. The 'user' side is always the
 * requester server-side, regardless of what's sent here -- only pass 'contact'.
 * @param {Object} contactData
 * @param {number} contactData.contact - User id of the contact to add
 * @returns {Promise} The created contact object
 */
export const createUserContact = async (contactData) => {
    try {
        const response = await api.post('/chat/user-contacts/', contactData);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Remove a contact from the authenticated user's own contact list
 * @param {number} contactId
 */
export const deleteUserContact = async (contactId) => {
    try {
        const response = await api.delete(`/chat/user-contacts/${contactId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Clear the authenticated user's own unread_message flag on this contact row (i.e. mark that DM
 * conversation as read) -- only the row's owner may do this.
 * @param {number} contactId - The caller's own UserContact row id for this conversation
 * @returns {Promise} The updated contact object
 */
export const markUserContactRead = async (contactId) => {
    try {
        const response = await api.patch(`/chat/user-contacts/${contactId}/`, { unread_message: false });
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Whether the authenticated user has any unread DM or group message (existence check only).
 * Used for the chat-toggle badge's initial state on page load, before the chat widget (and the
 * full contacts/group-members lists it loads) has ever been opened this session.
 * @returns {Promise<boolean>}
 */
export const getHasUnreadMessages = async () => {
    try {
        const response = await api.get('/chat/has-unread-messages/');
        return !!response.data?.has_unread;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// ============= Direct Messages API Functions =============

/**
 * Get the list of direct messages (Authenticated users -- filter client-side by user_contact)
 * @returns {Promise<Array>} Array of direct messages
 */
export const getDirectMessages = async () => {
    try {
        const response = await api.get('/chat/direct-messages/');
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// Builds a multipart FormData body when a File/Blob is present in the payload (e.g. a chat
// attachment), otherwise leaves the payload as plain JSON -- mirrors createGroupChat/
// updateGroupChat's group_photo handling below.
const toRequestBody = (data, fileKey) => {
    if (!(data?.[fileKey] instanceof File || data?.[fileKey] instanceof Blob)) return data;
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
        if (data[key] !== null && data[key] !== undefined) formData.append(key, data[key]);
    });
    return formData;
};

/**
 * Send a message through one of the authenticated user's own UserContact rows
 * @param {Object} messageData
 * @param {number} messageData.user_contact - Id of the sender's own UserContact row
 * @param {string} [messageData.message]
 * @param {File} [messageData.media] - Optional image/video attachment
 * @returns {Promise} The created message object
 */
export const createDirectMessage = async (messageData) => {
    try {
        const response = await api.post('/chat/direct-messages/', toRequestBody(messageData, 'media'));
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateDirectMessage = async (messageId, messageData) => {
    try {
        const response = await api.put(`/chat/direct-messages/${messageId}/`, toRequestBody(messageData, 'media'));
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteDirectMessage = async (messageId) => {
    try {
        const response = await api.delete(`/chat/direct-messages/${messageId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// ============= Group Chats API Functions =============

/**
 * Get the list of group chats (Authenticated users)
 * @returns {Promise<Array>} Array of group chats
 */
export const getGroupChats = async () => {
    try {
        const response = await api.get('/chat/group-chats/');
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a group chat -- the creator is automatically added as its first member (server-side).
 * @param {Object} groupChatData
 * @param {string} groupChatData.group_title
 * @param {File} [groupChatData.group_photo]
 * @param {string} [groupChatData.description]
 * @returns {Promise} The created group chat object
 */
export const createGroupChat = async (groupChatData) => {
    try {
        const config = {};
        let data = groupChatData;

        if (groupChatData?.group_photo instanceof File || groupChatData?.group_photo instanceof Blob) {
            const formData = new FormData();
            Object.keys(groupChatData).forEach(key => {
                if (groupChatData[key] !== null && groupChatData[key] !== undefined) {
                    formData.append(key, groupChatData[key]);
                }
            });
            data = formData;
        }

        const response = await api.post('/chat/group-chats/', data, config);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update a group chat. Replacing or clearing group_photo deletes the old file from storage
 * (handled server-side by GroupChatSerializer.update()).
 * @param {number} groupChatId
 * @param {Object} groupChatData
 * @returns {Promise} The updated group chat object
 */
export const updateGroupChat = async (groupChatId, groupChatData) => {
    try {
        const config = {};
        let data = groupChatData;

        if (groupChatData?.group_photo instanceof File || groupChatData?.group_photo instanceof Blob) {
            const formData = new FormData();
            Object.keys(groupChatData).forEach(key => {
                if (groupChatData[key] !== null && groupChatData[key] !== undefined) {
                    formData.append(key, groupChatData[key]);
                }
            });
            data = formData;
        }

        const response = await api.patch(`/chat/group-chats/${groupChatId}/`, data, config);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a group chat. Also deletes its group_photo from storage (handled server-side by
 * GroupChat.delete()).
 * @param {number} groupChatId
 */
export const deleteGroupChat = async (groupChatId) => {
    try {
        const response = await api.delete(`/chat/group-chats/${groupChatId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// ============= Group Chat Members API Functions =============

/**
 * Get the list of group chat memberships (Authenticated users -- filter client-side by group_chat)
 * @returns {Promise<Array>} Array of group chat members
 */
export const getGroupChatMembers = async () => {
    try {
        const response = await api.get('/chat/group-chat-members/');
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Add a member to a group chat -- only an existing member of that group may add others
 * @param {Object} memberData
 * @param {number} memberData.group_chat
 * @param {number} memberData.user
 * @returns {Promise} The created membership object
 */
export const createGroupChatMember = async (memberData) => {
    try {
        const response = await api.post('/chat/group-chat-members/', memberData);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Promote or demote a member's is_admin status -- admin members of that group only
 * @param {number} memberId
 * @param {boolean} isAdmin
 * @returns {Promise} The updated membership object
 */
export const updateGroupChatMember = async (memberId, isAdmin) => {
    try {
        const response = await api.patch(`/chat/group-chat-members/${memberId}/`, { is_admin: isAdmin });
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Clear the authenticated user's own unread_message flag on this membership row (i.e. mark that
 * group's conversation as read) -- only the member themself may do this, independently of the
 * is_admin-gated update above.
 * @param {number} memberId - The caller's own GroupChatMember row id for this group
 * @returns {Promise} The updated membership object
 */
export const markGroupChatMemberRead = async (memberId) => {
    try {
        const response = await api.patch(`/chat/group-chat-members/${memberId}/`, { unread_message: false });
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Remove a member from a group chat -- the member themself (leaving), or an admin member of
 * that same group
 * @param {number} memberId
 */
export const deleteGroupChatMember = async (memberId) => {
    try {
        const response = await api.delete(`/chat/group-chat-members/${memberId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// ============= Group Chat Messages API Functions =============

/**
 * Get the list of group chat messages (Authenticated users -- filter client-side by group_chat)
 * @returns {Promise<Array>} Array of group chat messages
 */
export const getGroupChatMessages = async () => {
    try {
        const response = await api.get('/chat/group-chat-messages/');
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Send a message to a group chat -- only an existing member of that group may post
 * @param {Object} messageData
 * @param {number} messageData.group_chat
 * @param {string} [messageData.message]
 * @param {File} [messageData.media] - Optional image/video attachment
 * @returns {Promise} The created message object
 */
export const createGroupChatMessage = async (messageData) => {
    try {
        const response = await api.post('/chat/group-chat-messages/', toRequestBody(messageData, 'media'));
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Edit a group chat message the authenticated user sent
 * @param {number} messageId
 * @param {Object} messageData
 */
export const updateGroupChatMessage = async (messageId, messageData) => {
    try {
        const response = await api.put(`/chat/group-chat-messages/${messageId}/`, toRequestBody(messageData, 'media'));
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a group chat message -- the sender, or any other existing member of that group
 * @param {number} messageId
 */
export const deleteGroupChatMessage = async (messageId) => {
    try {
        const response = await api.delete(`/chat/group-chat-messages/${messageId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// ============= Report Tickets API Functions =============
// User-filed bug/content reports, triaged by staff.

/**
 * Get report tickets -- staff get every ticket (optionally narrowed with filters.status),
 * everyone else gets only the tickets they themselves filed.
 * @param {Object} [filters]
 * @param {string} [filters.status] - 'open' | 'in_progress' | 'resolved' (staff only, ignored otherwise)
 * @param {number} [filters.id] - Narrow to a single ticket by id
 * @returns {Promise<Array>} Array of report tickets
 */
export const getReportTickets = async (filters = {}) => {
    try {
        const response = await api.get('/report-tickets/', { params: filters });
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * File a new report ticket. 'user' is always the requester server-side, and every new ticket
 * starts 'open' regardless of what's sent here.
 * @param {Object} reportData
 * @param {string} reportData.first_name
 * @param {string} reportData.last_name
 * @param {string} reportData.report_type - 'bug' | 'content' | 'other'
 * @param {string} reportData.description
 * @returns {Promise} The created report ticket object
 */
export const createReportTicket = async (reportData) => {
    try {
        const response = await api.post('/report-tickets/', reportData);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Edit a report ticket's own content (first_name/last_name/report_type/description) -- the
 * original reporter only.
 * @param {number} ticketId
 * @param {Object} reportData
 * @returns {Promise} The updated report ticket object
 */
export const updateReportTicket = async (ticketId, reportData) => {
    try {
        const response = await api.patch(`/report-tickets/${ticketId}/`, reportData);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Triage a report ticket's status -- staff only, independently of the reporter-editable fields
 * above (mirrors updateGroupChatMember/markGroupChatMemberRead's split for the same reason: two
 * different people are allowed to change two different things on the same row).
 * @param {number} ticketId
 * @param {string} statusValue - 'open' | 'in_progress' | 'resolved'
 * @returns {Promise} The updated report ticket object
 */
export const updateReportTicketStatus = async (ticketId, statusValue) => {
    try {
        const response = await api.patch(`/report-tickets/${ticketId}/`, { status: statusValue });
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Save the admin's written response on a report ticket -- staff only, independently of the
 * reporter-editable fields and the status change above (same split-gate reasoning).
 * @param {number} ticketId
 * @param {string} comment
 * @returns {Promise} The updated report ticket object
 */
export const updateReportTicketComment = async (ticketId, comment) => {
    try {
        const response = await api.patch(`/report-tickets/${ticketId}/`, { comment });
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Withdraw/dismiss a report ticket -- the reporter themself, or staff.
 * @param {number} ticketId
 */
export const deleteReportTicket = async (ticketId) => {
    try {
        const response = await api.delete(`/report-tickets/${ticketId}/`);
        return response.data;
    }
    catch (error) {
        throw error.response?.data || error.message;
    }
};

// ============= Chat WebSocket =============

// How long to wait before retrying a dropped connection (network blip, server restart) --
// fixed rather than exponential since chat is only ever open in one tab/widget at a time, so
// there's no thundering-herd risk to back off from.
const CHAT_SOCKET_RECONNECT_MS = 3000;

/**
 * Opens a WebSocket connection for real-time chat push events (see api/consumers.py). The
 * server never expects the client to send anything over this socket -- all chat writes still go
 * through the REST functions above; this only delivers small "something changed" events
 * (e.g. { event: 'direct_message', user_contact }, { event: 'group_message', group_chat },
 * { event: 'group_member_added'|'group_member_updated'|'group_member_removed', group_chat },
 * { event: 'group_updated'|'group_deleted', group_chat }, { event: 'contact_added' }) so the UI
 * knows what to refetch, rather than trusting a full serialized object pushed over the wire.
 *
 * Auto-reconnects on drop, matching the resilience the long-poll announcements loop already has
 * (see waitForNewAnnouncements/Notification.jsx) -- just via a real push transport instead.
 * @param {(event: {event: string, [key: string]: any}) => void} onEvent
 * @returns {() => void} Call to close the socket and stop reconnecting
 */
export const connectChatSocket = (onEvent) => {
    let socket = null;
    let closedByCaller = false;
    let reconnectTimeoutId = null;

    const connect = () => {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        socket = new WebSocket(`${WS_BASE_URL}/ws/chat/?token=${encodeURIComponent(token)}`);

        socket.onmessage = (message) => {
            try {
                onEvent(JSON.parse(message.data));
            } catch (error) {
                console.error('Error parsing chat WebSocket message:', error);
            }
        };

        socket.onclose = () => {
            if (closedByCaller) return;
            reconnectTimeoutId = setTimeout(connect, CHAT_SOCKET_RECONNECT_MS);
        };

        socket.onerror = () => {
            socket.close();
        };
    };

    connect();

    return () => {
        closedByCaller = true;
        if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
        socket?.close();
    };
};
